import type {
  CapabilityResultTrace,
  CapabilityTrace,
  ExposureDecision,
  McpOperationSource,
  OperationDescriptor,
  OperationRuntimeState,
  PolicyDecision,
  ProviderUse,
} from './velis-capability'

import { errorMessageFrom } from '@moeru/std'
import { getActivePinia } from 'pinia'
import { shallowRef } from 'vue'

import { useVelisCapabilityTraceStore } from '../stores/velis-capability-trace'
import {
  compactOperationResult,
  decideOperationPolicy,
  exposeOperations,
  firstPartyOperations,
  isGovernedOperationName,
  runtimeProfile,
} from './velis-capability'

interface CapabilityTurn {
  userText: string
  exposure: ExposureDecision
  blocked: Array<{ domain: string, effect: OperationDescriptor['effect'] }>
  approvals: Record<string, 'granted' | 'rejected'>
  trace: CapabilityTrace
}

const traces = shallowRef<CapabilityTrace[]>([])
let discovered: OperationDescriptor[] = []
let runtime: OperationRuntimeState[] = []
let sources: McpOperationSource[] = []
let pendingIds: string[] = []
let lastIds: string[] = []
let current: CapabilityTurn | undefined

export function capabilityTraceState() {
  return traces
}

/** Replaces MCP rows discovered from the weather server. Other servers are ignored. */
export function replaceDiscoveredOperations(input: {
  operations: readonly OperationDescriptor[]
  runtime: readonly OperationRuntimeState[]
  sources?: readonly McpOperationSource[]
}) {
  discovered = [...input.operations]
  runtime = [...input.runtime]
  sources = [...(input.sources ?? [])]
}

export function discoveredOperationSource(id: string) {
  const operation = discovered.find(item => item.id === id)
  if (!operation?.mcpName)
    return undefined
  return sources.find(item => item.name === operation.mcpName)
}

export function resetCapabilitySession() {
  discovered = []
  runtime = []
  sources = []
  pendingIds = []
  lastIds = []
  current = undefined
  traces.value = []
  publishTraces()
}

/**
 * Plans one chat turn and stores the trace.
 * The same user text keeps the current approval block list.
 */
export function planCapabilityTurn(text: string, providers: readonly ProviderUse[] = []): ExposureDecision {
  if (current?.userText === text)
    return current.exposure

  const exposure = exposeOperations({
    text,
    profile: runtimeProfile(),
    operations: [...firstPartyOperations(), ...discovered],
    runtime,
    pendingApprovalIds: pendingIds,
    continuationIds: lastIds,
  })
  lastIds = exposure.operations.map(item => item.id)
  const trace: CapabilityTrace = {
    providers: providers.map(item => ({ ...item })),
    candidateDomains: exposure.candidateDomains,
    operations: exposure.operations.map(item => ({
      id: item.id,
      source: item.source,
      effect: item.effect,
      risk: item.risk,
      approval: item.approval,
    })),
    unavailable: exposure.unavailable,
  }
  current = {
    userText: text,
    exposure,
    blocked: [],
    approvals: {},
    trace,
  }
  traces.value = [trace, ...traces.value].slice(0, 8)
  publishTraces()
  return exposure
}

/** Records a user approval decision for the open turn. A rejection blocks that effect. */
export function decideApproval(operationId: string, approval: 'granted' | 'rejected') {
  if (!current)
    return
  current.approvals[operationId] = approval
  if (approval === 'rejected')
    blockOperation(operationId)
  pendingIds = pendingIds.filter(id => id !== operationId)
}

/**
 * Authorizes one operation for the open turn.
 * Returns undefined when no capability turn is open, so legacy tools keep their old check.
 */
export function authorizeOperation(operationId: string): PolicyDecision | undefined {
  if (!current)
    return undefined
  const operation = [...firstPartyOperations(), ...discovered].find(item => item.id === operationId)
  if (!operation) {
    return { allowed: false, status: 'rejected', reason: 'unknown-operation' }
  }
  const decision = decideOperationPolicy({
    operation,
    userText: current.userText,
    approval: current.approvals[operationId] ?? 'none',
    blocked: current.blocked,
    exposedIds: current.exposure.operations.map(item => item.id),
  })
  if (decision.status === 'pending-confirmation' && !pendingIds.includes(operationId))
    pendingIds.push(operationId)
  if (decision.status === 'rejected')
    blockOperation(operationId)
  const approval = decision.status === 'allowed' ? 'allow' : decision.status
  current.trace = {
    ...current.trace,
    operations: current.trace.operations.map(item => item.id === operationId ? { ...item, approval } : item),
  }
  replaceLatestTrace(current.trace)
  return decision
}

/** Stores the compact result on the latest trace. */
export function recordOperationResult(result: CapabilityResultTrace) {
  if (!current)
    return
  current.trace = { ...current.trace, result }
  replaceLatestTrace(current.trace)
}

/** Compacts one result and writes the before/after sizes onto the trace. */
export async function observeOperation<T>(operationId: string, maxChars: number, run: () => Promise<T>): Promise<unknown> {
  const started = Date.now()
  try {
    const value = await run()
    const compact = compactOperationResult(value, maxChars)
    recordOperationResult({
      operationId,
      status: resultStatus(value),
      evidence: compact.evidence,
      rawChars: compact.rawChars,
      compactedChars: compact.compactedChars,
      latencyMs: Date.now() - started,
    })
    return compact.value
  }
  catch (error) {
    const message = errorMessageFrom(error) ?? 'The operation failed.'
    recordOperationResult({
      operationId,
      status: failureStatus(message),
      evidence: message.slice(0, 180),
      rawChars: message.length,
      compactedChars: Math.min(message.length, 180),
      latencyMs: Date.now() - started,
    })
    throw error
  }
}

/**
 * Runs one operation that the open turn exposed.
 * A missing turn keeps the old tool path. A rejection does not call the operation.
 */
export async function runExposedOperation(operationId: string, maxChars: number, run: () => Promise<unknown>) {
  const decision = authorizeOperation(operationId)
  if (decision && !decision.allowed) {
    return {
      isError: true,
      status: decision.status === 'pending-confirmation' ? 'requires_confirmation' : 'rejected',
      message: decision.reason,
    }
  }
  try {
    return await observeOperation(operationId, maxChars, run)
  }
  catch (error) {
    const message = errorMessageFrom(error) ?? 'The operation failed.'
    return {
      isError: true,
      status: failureStatus(message),
      content: [{ type: 'text', text: message }],
    }
  }
}

export function governedToolNames(names: readonly string[], exposedIds: readonly string[]) {
  const exposed = new Set(exposedIds)
  return names.filter(name => !isGovernedOperationName(name) || exposed.has(name))
}

function blockOperation(operationId: string) {
  if (!current)
    return
  const operation = [...firstPartyOperations(), ...discovered].find(item => item.id === operationId)
  if (!operation)
    return
  const exists = current.blocked.some(item => item.domain === operation.domain && item.effect === operation.effect)
  if (!exists)
    current.blocked.push({ domain: operation.domain, effect: operation.effect })
}

function replaceLatestTrace(trace: CapabilityTrace) {
  traces.value = [trace, ...traces.value.slice(1)]
  publishTraces()
}

function publishTraces() {
  const pinia = getActivePinia()
  if (!pinia)
    return
  // The settings window reads this snapshot. The stage window is the writer.
  useVelisCapabilityTraceStore(pinia).replace(traces.value.slice(0, 8))
}

function resultStatus(value: unknown) {
  if (typeof value !== 'object' || value === null)
    return 'succeeded'
  const record = value as { status?: unknown, isError?: unknown, content?: unknown, message?: unknown }
  const status = typeof record.status === 'string' ? record.status : ''
  if (status === 'requires_confirmation')
    return 'pending-confirmation'
  if (status === 'TIMEOUT' || status === 'timed-out')
    return 'timed-out'
  if (status === 'UNAVAILABLE' || status === 'unavailable')
    return 'unavailable'
  if (status === 'rejected')
    return 'rejected'
  const text = typeof record.message === 'string'
    ? record.message
    : textFromContent(record.content)
  if (record.isError === true || status === 'failed' || status === 'INVALID_INPUT')
    return failureStatus(text || status || 'failed')
  return 'succeeded'
}

function textFromContent(content: unknown) {
  if (!Array.isArray(content))
    return ''
  const part = content.find(item => typeof item === 'object' && item !== null && typeof (item as { text?: unknown }).text === 'string') as { text: string } | undefined
  return part?.text ?? ''
}

function failureStatus(message: string) {
  if (/timeout|timed out|aborted/i.test(message))
    return 'timed-out'
  if (/not running|not available|unavailable/i.test(message))
    return 'unavailable'
  return 'failed'
}
